/**
 * FinancialAnalysisPanel - Displays Financial Deep Dive results with Kill Sheet
 *
 * Features:
 * - Bull vs Bear thesis display
 * - Winning side verdict with confidence gauge
 * - Decisive factor explanation
 * - Data source timestamps for transparency
 * - Integrated Kill Sheet for rejected arguments
 */

import { useState } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Scale,
  Clock,
  Database,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Info,
  Target,
  Shield,
  Eye,
  Gauge,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { InfoTooltip, Tooltip } from '@/components/ui/Tooltip';
import { KillSheet } from './KillSheet';
import type { RejectedArgument, RejectionReason } from '@/types/researchModes';

// Investment stance enum - regulatory-safe, no explicit BUY/SELL
export type InvestmentStance = 'BULLISH' | 'LEANING_BULLISH' | 'NEUTRAL' | 'LEANING_BEARISH' | 'BEARISH';

// Watch item - trigger for thesis change
export interface WatchItem {
  trigger: string;
  impact: 'bull' | 'bear';
  timeframe: string;
  importance: 'high' | 'medium' | 'low';
}

// Financial Kill Sheet response from API
export interface FinancialKillSheet {
  ticker: string;
  analysis_date: string;
  winning_side: 'bull' | 'bear';
  confidence: number;
  bull_thesis: string;
  bear_thesis: string;
  // kill_sheet_reason is the new primary field, decisive_factor kept for backward compatibility
  kill_sheet_reason?: string;
  decisive_factor?: string;  // Deprecated, use kill_sheet_reason
  rejected_arguments: Array<{
    side: string;
    agent_role: string;
    agent_model: string;
    argument_summary: string;
    rejection_reason: string;
    rejection_detail: string;
    counter_evidence?: string;
    sources_cited: string[];
  }>;
  caveats: string;
  data_sources: Record<string, string>;
  research_depth: string;
  total_agents: number;
  debate_rounds: number;
  // Phase 4: Actionable insights (regulatory-safe)
  // API may return BUY/SELL/HOLD values - frontend maps to BULLISH/BEARISH
  stance?: InvestmentStance | string;
  bull_intensity?: number;  // 0-100
  bear_risk?: number;       // 0-100
  catalysts?: string[];
  risks?: string[];
  watch_items?: WatchItem[];
  // Structured evidence from debate
  key_evidence?: Array<{ source: string; claim: string; data_point: string; filing_reference?: string }>;
  // Structured caveats: conditions under which the verdict could flip
  structured_caveats?: string[];
}

// Head-to-head metric for comparison mode
export interface HeadToHeadMetric {
  metric_name: string;
  ticker_a_value: string;
  ticker_b_value: string;
  advantage: 'a' | 'b' | 'tie';
}

// Comparison Kill Sheet from 2-ticker comparison analysis
export interface ComparisonKillSheet {
  ticker_a: string;
  ticker_b: string;
  analysis_date: string;
  recommended_ticker: string;
  confidence: number;
  ticker_a_thesis: string;
  ticker_b_thesis: string;
  comparison_reason: string;
  head_to_head_metrics: HeadToHeadMetric[];
  relative_valuation: string;
  relative_moat: string;
  catalyst_comparison: string;
  risk_comparison: string;
  rejected_arguments: FinancialKillSheet['rejected_arguments'];
  caveats?: string;
  data_sources?: Record<string, string>;
  research_depth: string;
  total_agents: number;
  debate_rounds: number;
  stance_a?: string;
  stance_b?: string;
  catalysts_a?: string[];
  catalysts_b?: string[];
  risks_a?: string[];
  risks_b?: string[];
  watch_items?: WatchItem[];
  key_evidence?: Array<{ claim: string; source: string; confidence: number }>;
  // V2 comparison fields
  ticker_a_strengths?: string[];
  ticker_a_weaknesses?: string[];
  ticker_b_strengths?: string[];
  ticker_b_weaknesses?: string[];
  key_differentiators?: string[];
  decisive_factors?: string[];
}

// Anchored snapshot from hard data fetch
// Field names match the backend HardDataSnapshot.to_dict() output (camelCase)
export interface AnchoredSnapshot {
  ticker: string;
  companyName?: string;
  asOf?: string;           // timestamp field from backend
  status: string;
  price?: number;
  priceFormatted?: string;
  currency?: string;
  changePercent?: number;  // camelCase to match backend
  changePercentFormatted?: string;
  priceMovement?: string;
  marketCap?: number;      // camelCase to match backend
  marketCapFormatted?: string;
  peTTM?: number;          // camelCase to match backend
  peFormatted?: string;
  epsTTM?: number;         // camelCase to match backend
  epsFormatted?: string;
  peForward?: number;
  forwardEPS?: number;
  volume?: number;
  avgVolume?: number;
  dayHigh?: number;
  dayLow?: number;
  yearHigh?: number;       // camelCase to match backend
  yearLow?: number;        // camelCase to match backend
  fiftyTwoWeekPosition?: number;
  sourceUrls?: string[];
  provider?: string;
  errorMessage?: string;
  dataSourcesStatus?: Record<string, string>;

  // Analyst sentiment data
  analystRatings?: Array<{
    date: string;
    analystName: string;
    analystCompany: string;
    rating: string;
    priceTarget?: number;
  }>;
  priceTargets?: Array<{
    publishedDate: string;
    analystName: string;
    analystCompany: string;
    priceTarget: number;
  }>;
  consensusRating?: string;
  avgPriceTarget?: number;
  priceTargetUpside?: number;
  numAnalysts?: number;

  // Financial health scores (from FMP)
  altmanZScore?: number;
  altmanZRisk?: string;
  piotroskiScore?: number;
  piotroskiStrength?: string;

  // Recent news
  recentNews?: Array<{
    title: string;
    publishedDate?: string;
    published_date?: string;  // Alternative field name from yfinance
    site?: string;
    publisher?: string;       // From yfinance
    url?: string;
    link?: string;            // From yfinance
    source?: string;
  }>;

  // Yahoo Finance supplementary data (detailed breakdowns)
  yfRecommendations?: {
    strong_buy: number;
    buy: number;
    hold: number;
    sell: number;
    strong_sell: number;
    total: number;
    consensus: string;
  };
  yfPriceTargets?: {
    current: number;
    mean: number;
    high: number;
    low: number;
    median: number;
    num_analysts: number;
  };
  yfKeyStats?: {
    recommendation_key?: string;
    recommendation_mean?: number;
    num_analysts?: number;
    short_ratio?: number;
    short_pct_of_float?: number;
    shares_short?: number;
    shares_short_prior?: number;
    held_by_insiders?: number;
    held_by_institutions?: number;
    earnings_growth?: number;
    revenue_growth?: number;
    earnings_quarterly_growth?: number;
    forward_pe?: number;
    forward_eps?: number;
    dividend_yield?: number;
    payout_ratio?: number;
  };

  // Historical price data (5-year history from Yahoo Finance)
  historicalData?: {
    start_date: string;
    end_date: string;
    data_points: number;
    return_ytd?: number;
    return_1y?: number;
    return_3y?: number;
    return_5y?: number;
    volatility_1y?: number;
    high_52w?: number;
    low_52w?: number;
    avg_volume_3m?: number;
    prices?: Array<{
      date: string;
      open: number;
      high: number;
      low: number;
      close: number;
      volume: number;
    }>;
  };
}

// Helper to get the decisive factor with fallback
function getDecisiveFactor(killSheet: FinancialKillSheet): string {
  return killSheet.kill_sheet_reason || killSheet.decisive_factor || '';
}

interface FinancialAnalysisPanelProps {
  killSheet: FinancialKillSheet;
  anchoredSnapshot?: AnchoredSnapshot;
  className?: string;
  // Convergence & echo chamber data from streaming state
  convergenceTrajectory?: number[];
  initialDivergence?: number;
  echoChamberedDetected?: boolean;
}

// Map API rejection reasons to frontend RejectionReason type
function mapRejectionReason(reason: string): RejectionReason {
  const mapping: Record<string, RejectionReason> = {
    WEAK_EVIDENCE: 'REJECTED_SOURCE_TIER',
    CONTRADICTED_BY_DATA: 'REJECTED_CONTRADICTED',
    LOGICAL_FLAW: 'REJECTED_SPECULATIVE',
    OUTDATED_INFO: 'REJECTED_OUTDATED',
    CHERRY_PICKED: 'REJECTED_MAGNITUDE',
    UNFALSIFIABLE: 'REJECTED_SPECULATIVE',
  };
  return mapping[reason] || 'REJECTED_CONTRADICTED';
}

// Transform API rejected arguments to frontend format
function transformRejectedArguments(
  apiArgs: FinancialKillSheet['rejected_arguments']
): RejectedArgument[] {
  return apiArgs.map((arg, index) => ({
    id: `rejected-${index}`,
    argumentSummary: arg.argument_summary,
    arguedBy: arg.agent_role || `${arg.side} Agent`,
    agentModel: arg.agent_model,
    rejectionReason: mapRejectionReason(arg.rejection_reason),
    rejectionDetail: arg.rejection_detail,
    counterEvidence: arg.counter_evidence,
    sourcesCited: arg.sources_cited,
    taskIndex: 0,
  }));
}

// Stance configuration for display
const STANCE_CONFIG: Record<InvestmentStance, { label: string; color: string; bgColor: string }> = {
  BULLISH: { label: 'Bullish', color: 'text-green-400', bgColor: 'bg-green-500/20' },
  LEANING_BULLISH: { label: 'Leaning Bullish', color: 'text-green-300', bgColor: 'bg-green-500/10' },
  NEUTRAL: { label: 'Neutral', color: 'text-text-secondary', bgColor: 'bg-surface-light' },
  LEANING_BEARISH: { label: 'Leaning Bearish', color: 'text-red-300', bgColor: 'bg-red-500/10' },
  BEARISH: { label: 'Bearish', color: 'text-red-400', bgColor: 'bg-red-500/20' },
};

// Map API stance values (BUY/SELL) to regulatory-safe frontend values (BULLISH/BEARISH)
function mapApiStanceToFrontend(apiStance: string | undefined): InvestmentStance {
  if (!apiStance) return 'NEUTRAL';
  const normalized = apiStance.toUpperCase();
  switch (normalized) {
    case 'STRONG_BUY':
      return 'BULLISH';
    case 'BUY':
      return 'LEANING_BULLISH';
    case 'HOLD':
    case 'NEUTRAL':
      return 'NEUTRAL';
    case 'SELL':
      return 'LEANING_BEARISH';
    case 'STRONG_SELL':
      return 'BEARISH';
    // Also handle if frontend values are already sent
    case 'BULLISH':
    case 'LEANING_BULLISH':
    case 'LEANING_BEARISH':
    case 'BEARISH':
      return normalized as InvestmentStance;
    default:
      return 'NEUTRAL';
  }
}

// Importance configuration for watch items
const IMPORTANCE_CONFIG: Record<string, { color: string; bgColor: string }> = {
  high: { color: 'text-red-400', bgColor: 'bg-red-500/20' },
  medium: { color: 'text-amber-400', bgColor: 'bg-amber-500/20' },
  low: { color: 'text-text-secondary', bgColor: 'bg-surface-light' },
};

// Actionable Insights Panel Component
function ActionableInsightsPanel({ killSheet }: { killSheet: FinancialKillSheet }) {
  const [showWatchItems, setShowWatchItems] = useState(false);
  // Map API stance (BUY/SELL) to regulatory-safe frontend values (BULLISH/BEARISH)
  const stance = mapApiStanceToFrontend(killSheet.stance as string | undefined);
  const stanceConfig = STANCE_CONFIG[stance];
  const bullIntensity = killSheet.bull_intensity ?? 50;
  const bearRisk = killSheet.bear_risk ?? 50;
  const catalysts = killSheet.catalysts || [];
  const risks = killSheet.risks || [];
  const watchItems = killSheet.watch_items || [];

  return (
    <div className="rounded-xl border border-blue-500/30 bg-gradient-to-b from-blue-500/5 to-transparent overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-blue-500/20 bg-blue-500/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Gauge className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-400">Research Insights</h3>
              <p className="text-xs text-text-muted">Not investment advice</p>
            </div>
          </div>
          <Tooltip content={{
            BULLISH: "Strong evidence favoring this investment. The bull case convincingly outweighed the bear case.",
            LEANING_BULLISH: "Moderate evidence favoring the investment, but with notable risks worth monitoring.",
            NEUTRAL: "Evidence is roughly balanced. Neither side made a convincing case.",
            LEANING_BEARISH: "Moderate evidence against the investment. Risks outweighed the growth story.",
            BEARISH: "Strong evidence against this investment. The bear case convincingly outweighed the bull case.",
          }[stance]} position="left">
            <span className={cn('px-3 py-1 text-sm font-medium rounded-full cursor-help', stanceConfig.bgColor, stanceConfig.color)}>
              {stanceConfig.label}
            </span>
          </Tooltip>
        </div>
      </div>

      {/* Intensity Gauges */}
      <div className="p-4 grid md:grid-cols-2 gap-4">
        {/* Bull Intensity */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-green-400 flex items-center gap-1">
              <TrendingUp className="w-4 h-4" /> Bull Intensity <InfoTooltip content="Strength of the bull case on a 0-100 scale. Higher means stronger growth catalysts, undervaluation signals, and positive momentum." position="top" />
            </span>
            <span className="text-text-secondary">{bullIntensity}/100</span>
          </div>
          <div className="h-2 bg-surface-light rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-green-600 to-green-400 rounded-full transition-all duration-500"
              style={{ width: `${bullIntensity}%` }}
            />
          </div>
        </div>

        {/* Bear Risk */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-red-400 flex items-center gap-1">
              <Shield className="w-4 h-4" /> Risk Level <InfoTooltip content="Severity of risks on a 0-100 scale. Higher means more serious concerns about overvaluation, competition, or negative catalysts." position="top" />
            </span>
            <span className="text-text-secondary">{bearRisk}/100</span>
          </div>
          <div className="h-2 bg-surface-light rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-red-600 to-red-400 rounded-full transition-all duration-500"
              style={{ width: `${bearRisk}%` }}
            />
          </div>
        </div>
      </div>

      {/* Catalysts and Risks */}
      {(catalysts.length > 0 || risks.length > 0) && (
        <div className="px-4 pb-4 grid md:grid-cols-2 gap-4">
          {/* Catalysts */}
          {catalysts.length > 0 && (
            <div className="p-3 rounded-lg bg-green-500/5 border border-green-500/20">
              <h4 className="text-sm font-medium text-green-400 mb-2 flex items-center gap-1">
                <Target className="w-4 h-4" /> Catalysts
              </h4>
              <ul className="space-y-1">
                {catalysts.map((catalyst, i) => (
                  <li key={i} className="text-xs text-text-primary flex items-start gap-2">
                    <span className="text-green-500 mt-1">+</span>
                    {catalyst}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Risks */}
          {risks.length > 0 && (
            <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/20">
              <h4 className="text-sm font-medium text-red-400 mb-2 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" /> Key Risks
              </h4>
              <ul className="space-y-1">
                {risks.map((risk, i) => (
                  <li key={i} className="text-xs text-text-primary flex items-start gap-2">
                    <span className="text-red-500 mt-1">-</span>
                    {risk}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Watch Items */}
      {watchItems.length > 0 && (
        <div className="border-t border-blue-500/20">
          <button
            type="button"
            onClick={() => setShowWatchItems(!showWatchItems)}
            className="w-full flex items-center justify-between p-4 hover:bg-blue-500/5 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-medium text-blue-400">Watch List</span>
              <InfoTooltip content="Events that could change the investment thesis. If these triggers occur, the analysis may need to be re-run." position="right" />
              <span className="text-xs text-text-muted">({watchItems.length} triggers)</span>
            </div>
            {showWatchItems ? (
              <ChevronUp className="w-4 h-4 text-text-muted" />
            ) : (
              <ChevronDown className="w-4 h-4 text-text-muted" />
            )}
          </button>
          {showWatchItems && (
            <div className="px-4 pb-4 space-y-2">
              {watchItems.map((item, i) => {
                const impConfig = IMPORTANCE_CONFIG[item.importance] || IMPORTANCE_CONFIG.medium;
                return (
                  <div key={i} className="p-3 rounded-lg bg-surface-light/50 flex items-start gap-3">
                    <div className={cn('p-1 rounded', item.impact === 'bull' ? 'bg-green-500/20' : 'bg-red-500/20')}>
                      {item.impact === 'bull' ? (
                        <TrendingUp className="w-3 h-3 text-green-400" />
                      ) : (
                        <TrendingDown className="w-3 h-3 text-red-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-text-primary">{item.trigger}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-text-muted">{item.timeframe}</span>
                        <span className={cn('text-xs px-1.5 py-0.5 rounded', impConfig.bgColor, impConfig.color)}>
                          {item.importance}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Anchored Market Data Panel Component - Shows P/E, EPS, Price, Market Cap
function AnchoredMarketDataPanel({ snapshot }: { snapshot: AnchoredSnapshot }) {
  const changeColor = (snapshot.changePercent ?? 0) >= 0 ? 'text-green-400' : 'text-red-400';
  const changePrefix = (snapshot.changePercent ?? 0) >= 0 ? '+' : '';

  // Format timestamp from asOf field
  const timestamp = snapshot.asOf
    ? new Date(snapshot.asOf).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit', timeZoneName: 'short'
      })
    : 'Unknown';

  return (
    <div className="rounded-xl border border-purple-500/30 bg-gradient-to-b from-purple-500/5 to-transparent overflow-hidden">
      <div className="p-4 border-b border-purple-500/20 bg-purple-500/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/10">
              <Database className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h3 className="font-semibold text-purple-400">Anchored Market Data</h3>
              <p className="text-xs text-text-muted">Single source of truth - {snapshot.provider || 'FMP'}</p>
            </div>
          </div>
          <div className="text-xs text-text-muted flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {timestamp}
          </div>
        </div>
      </div>
      <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Price */}
        <div className="text-center p-3 rounded-lg bg-surface-light/50">
          <p className="text-xs text-text-muted mb-1">Price</p>
          <p className="text-lg font-bold text-text-primary">{snapshot.priceFormatted || (snapshot.price ? `$${snapshot.price.toFixed(2)}` : 'N/A')}</p>
          {snapshot.changePercent != null && (
            <p className={cn('text-xs', changeColor)}>{changePrefix}{snapshot.changePercent.toFixed(2)}%</p>
          )}
        </div>
        {/* Market Cap */}
        <div className="text-center p-3 rounded-lg bg-surface-light/50">
          <p className="text-xs text-text-muted mb-1">Market Cap</p>
          <p className="text-lg font-bold text-text-primary">{snapshot.marketCapFormatted || 'N/A'}</p>
        </div>
        {/* P/E Ratio */}
        <div className="text-center p-3 rounded-lg bg-surface-light/50">
          <p className="text-xs text-text-muted mb-1">P/E (TTM)</p>
          <p className="text-lg font-bold text-amber-400">{snapshot.peFormatted || (snapshot.peTTM ? `${snapshot.peTTM.toFixed(2)}x` : 'N/A')}</p>
        </div>
        {/* EPS */}
        <div className="text-center p-3 rounded-lg bg-surface-light/50">
          <p className="text-xs text-text-muted mb-1">EPS (TTM)</p>
          <p className="text-lg font-bold text-blue-400">{snapshot.epsFormatted || (snapshot.epsTTM ? `$${snapshot.epsTTM.toFixed(2)}` : 'N/A')}</p>
        </div>
      </div>
      {/* 52-week range if available */}
      {snapshot.yearHigh != null && snapshot.yearLow != null && snapshot.price != null && (
        <div className="px-4 pb-4">
          <div className="text-xs text-text-muted mb-2 flex justify-between">
            <span>52-Week Low: ${snapshot.yearLow.toFixed(2)}</span>
            <span>52-Week High: ${snapshot.yearHigh.toFixed(2)}</span>
          </div>
          <div className="h-2 bg-surface-light rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-red-500 via-amber-500 to-green-500 rounded-full"
              style={{
                width: `${Math.min(100, Math.max(0, ((snapshot.price - snapshot.yearLow) / (snapshot.yearHigh - snapshot.yearLow)) * 100))}%`
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export function FinancialAnalysisPanel({ killSheet, anchoredSnapshot, className, convergenceTrajectory, initialDivergence, echoChamberedDetected }: FinancialAnalysisPanelProps) {
  const [showDataSources, setShowDataSources] = useState(false);
  const isBullWinner = killSheet.winning_side === 'bull';
  const confidencePercent = Math.round(killSheet.confidence * 100);

  // Transform rejected arguments for KillSheet component
  const rejectedArguments = transformRejectedArguments(killSheet.rejected_arguments);
  const losingTitle = isBullWinner ? 'Why the Bear Case Failed' : 'Why the Bull Case Failed';

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header with Ticker and Date */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-green-500/10">
            <TrendingUp className="w-6 h-6 text-green-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-text-primary">
              {killSheet.ticker} Financial Deep Dive
            </h2>
            <p className="text-sm text-text-muted">
              {killSheet.analysis_date} | {killSheet.research_depth} depth |{' '}
              {killSheet.total_agents} agents | {killSheet.debate_rounds} debate round
              {killSheet.debate_rounds !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>

      {/* Anchored Market Data Panel - Shows P/E, EPS, Price, Market Cap */}
      {anchoredSnapshot && <AnchoredMarketDataPanel snapshot={anchoredSnapshot} />}

      {/* Verdict Banner */}
      <div
        className={cn(
          'p-6 rounded-xl border',
          isBullWinner
            ? 'bg-green-500/10 border-green-500/30'
            : 'bg-red-500/10 border-red-500/30'
        )}
      >
        <div className="flex items-center gap-4">
          <div
            className={cn(
              'p-3 rounded-full',
              isBullWinner ? 'bg-green-500/20' : 'bg-red-500/20'
            )}
          >
            {isBullWinner ? (
              <TrendingUp className="w-8 h-8 text-green-400" />
            ) : (
              <TrendingDown className="w-8 h-8 text-red-400" />
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  'text-2xl font-bold',
                  isBullWinner ? 'text-green-400' : 'text-red-400'
                )}
              >
                {isBullWinner ? 'BULL' : 'BEAR'} WINS
              </span>
              <span className="text-text-secondary">|</span>
              <span className="text-lg text-text-primary">{confidencePercent}% Confidence</span>
              {(echoChamberedDetected || (initialDivergence !== undefined && initialDivergence > 0 && initialDivergence < 0.3)) && (
                <Tooltip content="Agents showed high initial agreement — echo chamber risk detected. Confidence score has been adjusted." position="top">
                  <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-mono bg-amber-500/20 text-amber-400 cursor-help">
                    Echo Chamber
                  </span>
                </Tooltip>
              )}
            </div>
            <p className="text-sm text-text-secondary mt-1">
              {getDecisiveFactor(killSheet)}
            </p>
            {/* Convergence trajectory mini-display */}
            {convergenceTrajectory && convergenceTrajectory.length >= 2 && (
              <div className="flex items-center gap-3 mt-2">
                <span className="text-[10px] text-text-muted uppercase tracking-wider">Convergence</span>
                <svg width="120" height="20" viewBox="0 0 120 20" className="shrink-0">
                  <line x1="2" y1="10" x2="118" y2="10" stroke="rgb(var(--color-border))" strokeWidth={0.5} strokeDasharray="3 2" />
                  <polyline
                    fill="none"
                    stroke={convergenceTrajectory[convergenceTrajectory.length - 1] > convergenceTrajectory[0] + 0.05 ? '#10b981' : '#f59e0b'}
                    strokeWidth={1.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    points={convergenceTrajectory.map((v, i) => `${2 + (i / (convergenceTrajectory.length - 1)) * 116},${2 + (1 - v) * 16}`).join(' ')}
                  />
                </svg>
                <span className="text-[10px] font-mono text-text-secondary">
                  {(convergenceTrajectory[0] * 100).toFixed(0)}% → {(convergenceTrajectory[convergenceTrajectory.length - 1] * 100).toFixed(0)}%
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bull vs Bear Thesis */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Bull Thesis */}
        <div className="p-4 rounded-xl bg-green-500/5 border border-green-500/20">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-5 h-5 text-green-400" />
            <h3 className="font-semibold text-green-400">Bull Thesis</h3>
            {isBullWinner && (
              <CheckCircle2 className="w-4 h-4 text-green-400 ml-auto" />
            )}
          </div>
          <p className="text-sm text-text-primary leading-relaxed">
            {killSheet.bull_thesis}
          </p>
        </div>

        {/* Bear Thesis */}
        <div className="p-4 rounded-xl bg-red-500/5 border border-red-500/20">
          <div className="flex items-center gap-2 mb-3">
            <TrendingDown className="w-5 h-5 text-red-400" />
            <h3 className="font-semibold text-red-400">Bear Thesis</h3>
            {!isBullWinner && (
              <CheckCircle2 className="w-4 h-4 text-red-400 ml-auto" />
            )}
          </div>
          <p className="text-sm text-text-primary leading-relaxed">
            {killSheet.bear_thesis}
          </p>
        </div>
      </div>

      {/* Phase 4: Actionable Insights Panel */}
      {killSheet.stance && (
        <ActionableInsightsPanel killSheet={killSheet} />
      )}

      {/* Caveats */}
      {killSheet.caveats && (
        <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/20">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-amber-400 mb-1">Caveats</h3>
              <p className="text-sm text-text-primary leading-relaxed">
                {killSheet.caveats}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Kill Sheet - Rejected Arguments (Always Visible) */}
      <div className="rounded-xl border-2 border-red-500/30 bg-gradient-to-b from-red-500/5 to-transparent overflow-hidden">
        {/* Kill Sheet Header - Always Visible */}
        <div className="p-4 border-b border-red-500/20 bg-red-500/5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/10">
                <Scale className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h3 className="font-semibold text-red-400 flex items-center gap-1.5">Kill Sheet <InfoTooltip content="Arguments that were rejected during the debate because they relied on weak evidence, outdated data, or flawed reasoning. Think of it as the 'cutting room floor' of the analysis." position="right" /></h3>
                <p className="text-xs text-text-muted">{losingTitle}</p>
              </div>
            </div>
            {rejectedArguments.length > 0 && (
              <span className="px-2 py-1 text-xs bg-red-500/20 text-red-400 rounded-full">
                {rejectedArguments.length} argument{rejectedArguments.length !== 1 ? 's' : ''} rejected
              </span>
            )}
          </div>
        </div>

        {/* Kill Sheet Content */}
        {rejectedArguments.length > 0 ? (
          <KillSheet
            rejectedArguments={rejectedArguments}
            hideHeader={true}
          />
        ) : (
          <div className="p-6 text-center">
            <div className="flex flex-col items-center gap-2">
              <Info className="w-8 h-8 text-text-muted" />
              <p className="text-sm text-text-secondary">
                No arguments were explicitly rejected in this debate.
              </p>
              <p className="text-xs text-text-muted max-w-md">
                The mediator found both sides presented valid points, but determined the {' '}
                <span className={isBullWinner ? 'text-green-400' : 'text-red-400'}>
                  {isBullWinner ? 'Bull' : 'Bear'} case
                </span>{' '}
                was stronger based on the decisive factor above.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Dissent Record - Minority Position Preservation */}
      {(() => {
        const structuredCaveats = killSheet.structured_caveats || [];
        const losingSide = killSheet.winning_side === 'bull' ? 'bear' : 'bull';
        const losingWatchItems = (killSheet.watch_items || []).filter(w => w.impact === losingSide);
        const hasContent = structuredCaveats.length > 0 || losingWatchItems.length > 0;
        if (!hasContent) return null;
        return (
          <div className="rounded-xl border border-amber-500/30 bg-gradient-to-b from-amber-500/5 to-transparent overflow-hidden">
            <div className="p-4 border-b border-amber-500/20 bg-amber-500/5">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-amber-400">Dissent Record</h3>
                  <p className="text-xs text-text-muted">
                    Minority position preserved — conditions that could reverse the verdict
                  </p>
                </div>
              </div>
            </div>
            <div className="p-4 space-y-2.5">
              {structuredCaveats.map((caveat, i) => (
                <div key={`caveat-${i}`} className="flex items-start gap-2.5 text-sm">
                  <span className="text-amber-500 mt-0.5 font-bold shrink-0">!</span>
                  <span className="text-text-primary">{caveat}</span>
                </div>
              ))}
              {losingWatchItems.map((item, i) => (
                <div key={`watch-${i}`} className="flex items-start gap-2.5 text-sm">
                  <Eye className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                  <div>
                    <span className="text-text-primary">{item.trigger}</span>
                    {item.timeframe && (
                      <span className="text-xs text-text-muted ml-2">{item.timeframe}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Data Sources */}
      <div className="rounded-xl border border-border overflow-hidden">
        <button
          type="button"
          onClick={() => setShowDataSources(!showDataSources)}
          className="w-full flex items-center justify-between p-4 hover:bg-surface-light/30 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Database className="w-5 h-5 text-text-secondary" />
            <span className="text-sm font-medium text-text-primary">Data Sources</span>
            <span className="text-xs text-text-muted">
              ({Object.keys(killSheet.data_sources).length} sources)
            </span>
          </div>
          {showDataSources ? (
            <ChevronUp className="w-4 h-4 text-text-muted" />
          ) : (
            <ChevronDown className="w-4 h-4 text-text-muted" />
          )}
        </button>
        {showDataSources && (
          <div className="px-4 pb-4 space-y-2">
            {Object.entries(killSheet.data_sources).map(([source, timestamp]) => (
              <div
                key={source}
                className="flex items-center justify-between py-2 px-3 bg-surface/50 rounded-lg"
              >
                <span className="text-sm text-text-primary uppercase">{source}</span>
                <div className="flex items-center gap-2 text-xs text-text-muted">
                  <Clock className="w-3 h-3" />
                  {timestamp}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default FinancialAnalysisPanel;
